import React, { useRef } from 'react';

/**
 * ShipmentBillUpload - The bill document attached to a shipment.
 *
 * A shipment cannot be marked as shipped until a bill is uploaded here.
 * Once the shipment IS shipped, the bill becomes read-only - it is part of
 * the shipping record.
 */
const ShipmentBillUpload = ({
  shipment,
  onBillUpload,
  onBillDelete,
  uploadingBill,
  formatDate
}) => {
  const fileInputRef = useRef(null);

  const isShipped = shipment?.status === 'shipped';
  const hasBill = !!shipment?.bill_path;
  const isPdf = shipment?.bill_mime_type === 'application/pdf';

  const getFileUrl = (filePath) =>
    `${process.env.REACT_APP_API_URL?.replace('/api', '') || ''}/uploads/${filePath}`;

  const triggerFilePicker = () => fileInputRef.current?.click();

  const handleFileChange = (event) => {
    onBillUpload(event);
    // Reset so picking the same file twice in a row still fires onChange
    event.target.value = '';
  };

  return (
    <div className="shipment-bill-section">
      <div className="shipment-bill-header">
        <h3>
          Bill
          {!hasBill && !isShipped && (
            <span className="shipment-bill-required"> — required to ship</span>
          )}
        </h3>
      </div>

      {hasBill ? (
        <div className="shipment-bill-attached">
          <a
            className="shipment-bill-preview"
            href={getFileUrl(shipment.bill_path)}
            target="_blank"
            rel="noopener noreferrer"
          >
            {isPdf ? (
              <span className="shipment-bill-pdf-chip">PDF</span>
            ) : (
              <img
                src={getFileUrl(shipment.bill_path)}
                alt="Shipment bill"
                className="shipment-bill-thumb"
              />
            )}
          </a>

          <div className="shipment-bill-meta">
            <a
              className="shipment-bill-filename"
              href={getFileUrl(shipment.bill_path)}
              target="_blank"
              rel="noopener noreferrer"
            >
              {shipment.bill_filename || 'Bill'}
            </a>
            <div className="shipment-bill-uploaded-by">
              Uploaded by {shipment.bill_uploaded_by_name || 'unknown'}
              {shipment.bill_uploaded_at && ` · ${formatDate(shipment.bill_uploaded_at)}`}
            </div>

            {!isShipped && (
              <div className="shipment-bill-actions">
                <button
                  type="button"
                  className="shipment-bill-btn replace"
                  onClick={triggerFilePicker}
                  disabled={uploadingBill}
                >
                  {uploadingBill ? 'Uploading...' : 'Replace'}
                </button>
                <button
                  type="button"
                  className="shipment-bill-btn remove"
                  onClick={onBillDelete}
                  disabled={uploadingBill}
                >
                  Remove
                </button>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="shipment-bill-empty">
          {isShipped ? (
            <span className="shipment-bill-none">No bill on file</span>
          ) : (
            <>
              <button
                type="button"
                className="shipment-bill-btn upload"
                onClick={triggerFilePicker}
                disabled={uploadingBill}
              >
                {uploadingBill ? 'Uploading...' : 'Upload Bill'}
              </button>
              <span className="shipment-bill-hint">
                Photo or PDF · max 10MB
              </span>
            </>
          )}
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/webp,application/pdf"
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />
    </div>
  );
};

export default ShipmentBillUpload;
